import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectsApi } from "../../api/api";
import { Button } from "../../components/ui";
import { useBuilderIdentity } from "./useBuilderIdentity";
import styles from "./WorkflowBuilder.module.css";

type ClientOption = { id: string; name: string };
type Props = {
    value: string | null;
    currentName?: string | null;
    disabled: boolean;
    onChange: (projectId: string | null) => void;
    label?: string;
};

export function WorkflowPreviewClientPicker({ value, currentName, disabled, onChange, label }: Props) {
    const { t, i18n } = useTranslation();
    const { resolveToken, scope, switching } = useBuilderIdentity();
    const id = useId();
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let active = true;
        setClients([]); setLoading(true); setError("");
        if (!switching) void (async () => {
            try {
                const token = await resolveToken();
                if (!active) return;
                const result = await getProjectsApi(token);
                if (active) setClients(result.projects.map(project => ({ id: project.id, name: project.name })));
            } catch (caught: unknown) {
                if (active) setError(caught instanceof Error ? caught.message : t("workflowBuilder.clientLoadError"));
            } finally { if (active) setLoading(false); }
        })();
        return () => { active = false; };
    }, [resolveToken, scope, switching, attempt, t]);
    const options = [...clients].sort((left, right) => left.name.localeCompare(right.name, i18n.language));
    return <div className={styles.previewClient}>
        <label htmlFor={id}>{label ?? t("workflowBuilder.previewClient")}</label>
        <select id={id} value={value ?? ""} disabled={disabled || loading || switching}
            onChange={event => onChange(event.target.value || null)}>
            <option value="">{t(loading ? "workflowBuilder.loadingClients" : "workflowBuilder.selectClient")}</option>
            {value && !options.some(client => client.id === value) && <option value={value}>{currentName || t("workflowBuilder.selectedClient")}</option>}
            {options.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
        </select>
        {loading && <small role="status">{t("workflowBuilder.loadingClients")}</small>}
        {!loading && !error && clients.length === 0 && <small>{t("workflowBuilder.noClients")}</small>}
        {error && <div role="alert"><span>{error}</span><Button variant="ghost" size="sm" onClick={() => setAttempt(count => count + 1)}>{t("workflowBuilder.retry")}</Button></div>}
    </div>;
}
